/**
 * PRODUCT-154 — emits every `--wh-*` custom property for both themes.
 *
 * Mounted first in `src/App/index.tsx` so the variables exist before any styled
 * component that reads them (via `color-theme.ts`) paints. Selectors are the
 * bare attribute form (`[data-theme='dark']`, not `html[data-theme='dark']`) so
 * any subtree can re-scope itself — a force-light island is just
 * `<div data-theme="light">`, and the print block pins light for print media.
 * The print block is NOT enough on its own: headless PDF capture renders
 * screen media, so a PDF surface also needs the explicit island (see
 * `src/Browserless/TicketBrowserless/index.tsx`).
 *
 * Dark is LIVE: `src/theme/mode.ts` and the boot script in `index.html` both
 * write `<html data-theme>`, so the dark block below is what a user who picks
 * Dark actually gets. The shadcn semantic tokens (`--background`, `--card`, …)
 * are keyed on the SAME selectors in `src/styles/tailwind.css` — deliberately,
 * so the two token systems flip together and re-scope together. Change the
 * selector pairing here and change it there, or half the app follows the theme
 * and half does not — which is exactly the state PRO-736 replaced.
 */
import { createGlobalStyle } from 'styled-components'

import { DARK, LIGHT, PALETTE_TOKENS, tokenToCssVar } from './palette'

function declarations(theme: Record<string, string>): string {
  return PALETTE_TOKENS.map(
    (token) => `${tokenToCssVar(token)}: ${theme[token]};`
  ).join('\n    ')
}

const LIGHT_DECLS = declarations(LIGHT)
const DARK_DECLS = declarations(DARK)

export const ThemeVars = createGlobalStyle`
  :root,
  [data-theme='light'] {
    color-scheme: light;
    ${LIGHT_DECLS}
  }

  [data-theme='dark'] {
    color-scheme: dark;
    ${DARK_DECLS}
  }

  @media print {
    :root {
      color-scheme: light;
      ${LIGHT_DECLS}
    }
  }
`
